import { useApolloClient } from '@apollo/client'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Add, FileFilled, File } from '@factorialco/f0-react/icons/app'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  AtsVacanciesJobPostingsFilterDocument,
  AtsVacanciesLineManagersFilterDocument,
  AtsVacanciesLocationsFilterDocument,
  AtsVacanciesTableDocument,
  AtsVacanciesTableItemFragment,
  AtsVacanciesTeamsFilterDocument,
  AtsVacancyOriginTypesEnum,
  AtsVacancyStatusesEnum,
  AtsVacancyTypesEnum,
} from 'generated/resources/graphql'
import {
  getDaysOpen,
  getStatusVariant,
  renderAvatarList,
  renderDeadline,
  renderPerson,
} from 'modules/ats/lib/utils/vacancies'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useGraphqlDataSource } from 'modules/core/hooks/useGraphqlDataSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import { ActivateVacanciesDialog } from './ActivateVacancies'
import { AssignJobPostingModal } from './AssignJobPosting'
import { AssignHiringManagersModal } from './AssignHiringManagers'
import { AssignRecruitersModal } from './AssignRecruitersModal'
import { CancelVacanciesDialog } from './CancelVacancies'

const VACANCIES_MODAL = {
  ACTIVATE_VACANCY: 'activate-vacancy',
  ASSIGN_JOB_POSTING: 'assign-job-posting',
  ASSIGN_HIRING_MANAGERS: 'assign-hiring-managers',
  ASSIGN_RECRUITERS: 'assign-recruiters',
  CANCEL_VACANCY: 'cancel-vacancy',
} as const

type VacanciesModal = (typeof VACANCIES_MODAL)[keyof typeof VACANCIES_MODAL] | null

type JobPostingFilterNode = {
  id: string
  title: string
  [key: string]: unknown
}

type VacancyItem = AtsVacanciesTableItemFragment & {
  teamId: string | null
  locationId: string | null
}

export const VacanciesTable = () => {
  const client = useApolloClient()
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const [selectedVacancyIds, setSelectedVacancyIds] = useState<string[] | null>(null)
  const clearSelectedItemsRef = useRef<(() => void) | null>(null)
  const [showModal, setModal] = useState<VacanciesModal>(null)
  const closeModal = () => setModal(null)
  const teamNamesById = useRef<Record<string, string>>({})
  const locationNamesById = useRef<Record<string, string>>({})
  const [items, setItems] = useState<VacancyItem[]>([])

  const getGroupCount = useCallback(
    (field: keyof VacancyItem) => (groupId: unknown) =>
      items.filter((item) => String(item[field] || undefined) === String(groupId)).length,
    [items]
  )

  const grouping = useMemo(
    () => ({
      collapsible: true as const,
      mandatory: false,
      defaultOpenGroups: true,
      groupBy: {
        teamId: {
          name: i18n.t('ats.vacancies.table.columns.team'),
          label: (groupId: unknown) =>
            groupId === null
              ? i18n.t('ats.vacancies.table.no_team')
              : (teamNamesById.current[String(groupId)] ?? String(groupId)),
          defaultDirection: 'asc' as const,
          itemCount: getGroupCount('teamId'),
        },
        locationId: {
          name: i18n.t('ats.vacancies.table.columns.market'),
          label: (groupId: unknown) =>
            groupId === null
              ? i18n.t('ats.vacancies.table.no_market')
              : (locationNamesById.current[String(groupId)] ?? String(groupId)),
          defaultDirection: 'asc' as const,
          itemCount: getGroupCount('locationId'),
        },
        status: {
          name: i18n.t('ats.vacancies.table.columns.status'),
          label: (groupId: unknown) => {
            const statusKey = String(groupId)
            const item = items.find((i) => i.status === statusKey)
            return item?.statusLabel ?? i18n.t('ats.vacancies.table.no_status')
          },
          defaultDirection: 'asc' as const,
          itemCount: getGroupCount('status'),
        },
      },
    }),
    [getGroupCount, items]
  )

  useEffect(() => {
    if (showModal === null) {
      setSelectedVacancyIds(null)
    }
  }, [showModal])

  const jobPostingFilterSource = useGraphqlDataSource(
    {
      document: AtsVacanciesJobPostingsFilterDocument,
      paginationType: 'infinite-scroll',
      perPage: 20,
      fetchPolicy: 'cache-first',
      search: { enabled: true },
      mapVariables: ({ search }) => ({
        title: search || undefined,
      }),
      mapData: (data): JobPostingFilterNode[] => data.ats.jobPostingsConnection.nodes,
      mapPagination: (data) => ({
        total: data.ats.jobPostingsConnection.nodes.length,
        cursor: data.ats.jobPostingsConnection.pageInfo.endCursor ?? undefined,
        hasNextPage: data.ats.jobPostingsConnection.pageInfo.hasNextPage,
      }),
    },
    []
  )

  const source = useGraphqlDataCollectionSource({
    document: AtsVacanciesTableDocument,
    mapData: (data): VacancyItem[] => {
      const mapped = data.ats.vacanciesConnection.nodes.map((node) => {
        if (node.team) {
          teamNamesById.current[node.team.id] = node.team.name
        }
        if (node.location) {
          locationNamesById.current[node.location.id] = node.location.name
        }
        return {
          ...node,
          teamId: node.team?.id ?? null,
          locationId: node.location?.id ?? null,
        }
      })
      setItems(mapped)
      return mapped
    },
    mapPagination: (data) => ({
      total: data.ats.vacanciesConnection.totalCount ?? 0,
    }),
    mapVariables: ({ search, filters }) => {
      const statusArr = Array.isArray(filters.status) ? filters.status : undefined
      const vacancyTypeArr = Array.isArray(filters.vacancyType) ? filters.vacancyType : undefined
      const originTypeArr = Array.isArray(filters.originType) ? filters.originType : undefined
      const lineManagerArr = Array.isArray(filters.lineManager) ? filters.lineManager : undefined
      const jobPostingArr = Array.isArray(filters.jobPosting) ? filters.jobPosting : undefined
      const teamArr = Array.isArray(filters.team) ? filters.team : undefined
      const locationArr = Array.isArray(filters.location) ? filters.location : undefined

      return {
        search,
        statuses: statusArr?.length
          ? statusArr.filter((v): v is AtsVacancyStatusesEnum => typeof v === 'string')
          : undefined,
        vacancyTypes: vacancyTypeArr?.length
          ? vacancyTypeArr.filter((v): v is AtsVacancyTypesEnum => typeof v === 'string')
          : undefined,
        originTypes: originTypeArr?.length
          ? originTypeArr.filter((v): v is AtsVacancyOriginTypesEnum => typeof v === 'string')
          : undefined,
        lineManagerId: lineManagerArr?.[0] ? String(lineManagerArr[0]) : undefined,
        atsJobPostingIds: jobPostingArr?.length ? jobPostingArr.map(String) : undefined,
        teamId: teamArr?.[0] ? String(teamArr[0]) : undefined,
        locationId: locationArr?.[0] ? String(locationArr[0]) : undefined,
      }
    },
    search: { enabled: true },
    filters: {
      status: {
        label: i18n.t('ats.vacancies.table.columns.status'),
        type: 'in',
        options: {
          options: [
            { label: i18n.t('ats.vacancies.table.filters.status.to_do'), value: 'to_do' },
            {
              label: i18n.t('ats.vacancies.table.filters.status.in_progress'),
              value: 'in_progress',
            },
            { label: i18n.t('ats.vacancies.table.filters.status.offer'), value: 'offer' },
            { label: i18n.t('ats.vacancies.table.filters.status.hired'), value: 'hired' },
            { label: i18n.t('ats.vacancies.table.filters.status.cancelled'), value: 'cancelled' },
          ],
        },
      },
      vacancyType: {
        label: i18n.t('ats.vacancies.table.columns.vacancy_type'),
        type: 'in',
        options: {
          options: [
            {
              label: i18n.t('ats.vacancies.table.filters.vacancy_type.backfill'),
              value: 'backfill',
            },
            {
              label: i18n.t('ats.vacancies.table.filters.vacancy_type.new_position'),
              value: 'new_position',
            },
          ],
        },
      },
      originType: {
        label: i18n.t('ats.vacancies.table.columns.origin'),
        type: 'in',
        options: {
          options: [
            {
              label: i18n.t('ats.vacancies.table.filters.origin_type.requisition'),
              value: 'requisition',
            },
            { label: i18n.t('ats.vacancies.table.filters.origin_type.manual'), value: 'manual' },
          ],
        },
      },
      lineManager: {
        label: i18n.t('ats.vacancies.table.columns.line_manager'),
        type: 'in',
        options: {
          cache: true,
          options: async () => {
            const result = await client.query({
              query: AtsVacanciesLineManagersFilterDocument,
              fetchPolicy: 'cache-first',
            })

            const seen = new Set<string>()
            const options: Array<{ label: string; value: string }> = []

            for (const node of result.data?.ats.vacanciesConnection.nodes ?? []) {
              if (node.lineManager && !seen.has(node.lineManager.id)) {
                seen.add(node.lineManager.id)
                options.push({
                  label: `${node.lineManager.firstName} ${node.lineManager.lastName}`.trim(),
                  value: node.lineManager.id,
                })
              }
            }

            return options.sort((a, b) => a.label.localeCompare(b.label))
          },
        },
      },
      jobPosting: {
        label: i18n.t('ats.vacancies.table.columns.job_opening'),
        type: 'in',
        options: {
          source: jobPostingFilterSource,
          mapOptions: (jp) => ({ label: jp.title, value: jp.id }),
        },
      },
      team: {
        label: i18n.t('ats.vacancies.table.columns.team'),
        type: 'in',
        options: {
          cache: true,
          options: async () => {
            const result = await client.query({
              query: AtsVacanciesTeamsFilterDocument,
              fetchPolicy: 'cache-first',
            })

            const seen = new Set<string>()
            const options: Array<{ label: string; value: string }> = []

            for (const node of result.data?.ats.vacanciesConnection.nodes ?? []) {
              if (node.team && !seen.has(node.team.id)) {
                seen.add(node.team.id)
                options.push({ label: node.team.name, value: node.team.id })
              }
            }

            return options.sort((a, b) => a.label.localeCompare(b.label))
          },
        },
      },
      location: {
        label: i18n.t('ats.vacancies.table.columns.market'),
        type: 'in',
        options: {
          cache: true,
          options: async () => {
            const result = await client.query({
              query: AtsVacanciesLocationsFilterDocument,
              fetchPolicy: 'cache-first',
            })

            const seen = new Set<string>()
            const options: Array<{ label: string; value: string }> = []

            for (const node of result.data?.ats.vacanciesConnection.nodes ?? []) {
              if (node.location && !seen.has(node.location.id)) {
                seen.add(node.location.id)
                options.push({ label: node.location.name, value: node.location.id })
              }
            }

            return options.sort((a, b) => a.label.localeCompare(b.label))
          },
        },
      },
    },
    presets: [
      {
        label: i18n.t('ats.vacancies.table.filters.status.to_do'),
        filter: { status: ['to_do'] },
      },
      {
        label: i18n.t('ats.vacancies.table.filters.status.in_progress'),
        filter: { status: ['in_progress'] },
      },
      {
        label: i18n.t('ats.vacancies.table.filters.status.offer'),
        filter: { status: ['offer'] },
      },
      {
        label: i18n.t('ats.vacancies.table.filters.status.hired'),
        filter: { status: ['hired'] },
      },
      {
        label: i18n.t('ats.vacancies.table.filters.vacancy_type.backfill'),
        filter: { vacancyType: ['backfill'] },
      },
      {
        label: i18n.t('ats.vacancies.table.filters.vacancy_type.new_position'),
        filter: { vacancyType: ['new_position'] },
      },
      {
        label: i18n.t('ats.vacancies.table.filters.origin_type.requisition'),
        filter: { originType: ['requisition'] },
      },
      {
        label: i18n.t('ats.vacancies.table.filters.origin_type.manual'),
        filter: { originType: ['manual'] },
      },
    ],
    itemUrl: ({ id }) =>
      navigation.ats.hiringPlan.vacancies.vacancyDetail.fullPath({ vacancy_id: id }),
    // TODO: Who can create vacancies?
    primaryActions: () => ({
      label: i18n.t('ats.vacancies.new'),
      icon: Add,
      onClick: () => navigate(navigation.ats.hiringPlan.vacancies.new.fullPath()),
    }),
    selectable: (item) => String(item.id),
    grouping,
    itemActions: (item: AtsVacanciesTableItemFragment) => [
      {
        label: i18n.t('ats.vacancies.assign_job'),
        onClick: () => {
          setSelectedVacancyIds([item.id])
          setModal(VACANCIES_MODAL.ASSIGN_JOB_POSTING)
        },
      },
      {
        label: i18n.t('ats.vacancies.assign_hiring_managers'),
        onClick: () => {
          setSelectedVacancyIds([item.id])
          setModal(VACANCIES_MODAL.ASSIGN_HIRING_MANAGERS)
        },
      },
      {
        label: i18n.t('ats.vacancies.assign_recruiters'),
        onClick: () => {
          setSelectedVacancyIds([item.id])
          setModal(VACANCIES_MODAL.ASSIGN_RECRUITERS)
        },
      },
      ...(item.permissionsFor.includes('cancel')
        ? [
            {
              label: i18n.t('ats.vacancies.cancel_vacancy'),
              onClick: () => {
                setSelectedVacancyIds([item.id])
                setModal(VACANCIES_MODAL.CANCEL_VACANCY)
              },
            },
          ]
        : []),
      ...(item.permissionsFor.includes('reopen')
        ? [
            {
              label: i18n.t('ats.vacancies.activate_vacancy'),
              onClick: () => {
                setSelectedVacancyIds([item.id])
                setModal(VACANCIES_MODAL.ACTIVATE_VACANCY)
              },
            },
          ]
        : []),
    ],
    bulkActions: (selectedItems) => {
      const checkedItems = selectedItems.itemsStatus.filter((status) => status.checked)
      const hasCancellableItems = checkedItems.some((status) =>
        status.item.permissionsFor.includes('cancel')
      )
      const hasActivatableItems = checkedItems.some((status) =>
        status.item.permissionsFor.includes('reopen')
      )

      return {
        primary: [
          {
            label: i18n.t('ats.vacancies.assign_job'),
            id: 'assign_job',
            keepSelection: true,
          },
        ],
        secondary: [
          {
            label: i18n.t('ats.vacancies.assign_hiring_managers'),
            id: 'assign_hiring_managers',
            keepSelection: true,
          },
          {
            label: i18n.t('ats.vacancies.assign_recruiters'),
            id: 'assign_recruiters',
            keepSelection: true,
          },
          ...(hasCancellableItems
            ? [
                {
                  label: i18n.t('ats.vacancies.cancel_vacancy'),
                  id: 'cancel_vacancy',
                  keepSelection: true,
                },
              ]
            : []),
          ...(hasActivatableItems
            ? [
                {
                  label: i18n.t('ats.vacancies.activate_vacancy'),
                  id: 'activate_vacancy' as const,
                  keepSelection: true,
                },
              ]
            : []),
        ],
      }
    },
  })

  return (
    <>
      <OneDataCollection
        source={source}
        onBulkAction={(action, selectedItems, clearSelectedItems) => {
          const ids = selectedItems.itemsStatus
            .filter((status) => status.checked)
            .map((status) => status.item.id)

          clearSelectedItemsRef.current = clearSelectedItems

          if (action === 'assign_job') {
            setSelectedVacancyIds(ids)
            setModal(VACANCIES_MODAL.ASSIGN_JOB_POSTING)
          }

          if (action === 'assign_hiring_managers') {
            setSelectedVacancyIds(ids)
            setModal(VACANCIES_MODAL.ASSIGN_HIRING_MANAGERS)
          }

          if (action === 'assign_recruiters') {
            setSelectedVacancyIds(ids)
            setModal(VACANCIES_MODAL.ASSIGN_RECRUITERS)
          }

          if (action === 'cancel_vacancy') {
            setSelectedVacancyIds(ids)
            setModal(VACANCIES_MODAL.CANCEL_VACANCY)
          }

          if (action === 'activate_vacancy') {
            setSelectedVacancyIds(ids)
            setModal(VACANCIES_MODAL.ACTIVATE_VACANCY)
          }
        }}
        visualizations={[
          {
            type: 'table',
            options: {
              frozenColumns: 1,
              columns: [
                {
                  label: i18n.t('ats.vacancies.table.columns.name'),
                  render: (item: AtsVacanciesTableItemFragment) => item.name,
                },
                {
                  label: i18n.t('ats.vacancies.table.columns.recruiter'),
                  render: (item: AtsVacanciesTableItemFragment) =>
                    renderAvatarList(item.recruiters),
                },
                {
                  label: i18n.t('ats.vacancies.table.columns.hiring_managers'),
                  render: (item: AtsVacanciesTableItemFragment) =>
                    renderAvatarList(item.atsVacancyHiringManagers),
                },
                {
                  label: i18n.t('ats.vacancies.table.columns.line_manager'),
                  render: (item: AtsVacanciesTableItemFragment) => renderPerson(item.lineManager),
                },
                {
                  label: i18n.t('ats.vacancies.table.columns.job_opening'),
                  render: (item: AtsVacanciesTableItemFragment) => item.jobPosting?.title ?? '-',
                },
                {
                  label: i18n.t('ats.vacancies.table.columns.status'),
                  render: (item: AtsVacanciesTableItemFragment) => {
                    const variant = getStatusVariant(item.status)
                    return variant
                      ? {
                          type: 'status' as const,
                          value: { label: item.statusLabel, status: variant },
                        }
                      : '-'
                  },
                },
                {
                  label: i18n.t('ats.vacancies.table.columns.days_open'),
                  render: (item: AtsVacanciesTableItemFragment) => {
                    const days = getDaysOpen(item.processStartedAt)
                    return days !== null
                      ? { type: 'tag' as const, value: { label: String(days) } }
                      : '-'
                  },
                },
                {
                  label: i18n.t('ats.vacancies.table.columns.team'),
                  render: (item: AtsVacanciesTableItemFragment) =>
                    item.team?.name
                      ? { type: 'tag' as const, value: { label: item.team.name } }
                      : '-',
                },
                {
                  label: i18n.t('ats.vacancies.table.columns.market'),
                  render: (item: AtsVacanciesTableItemFragment) =>
                    item.location?.name
                      ? { type: 'tag' as const, value: { label: item.location.name } }
                      : '-',
                },
                {
                  label: i18n.t('ats.vacancies.table.columns.deadline'),
                  render: (item: AtsVacanciesTableItemFragment) => renderDeadline(item.deadline),
                },
                {
                  label: i18n.t('ats.vacancies.table.columns.vacancy_type'),
                  render: (item: AtsVacanciesTableItemFragment) => {
                    if (!item.vacancyType) return '-'
                    const color: 'flubber' | 'purple' =
                      item.vacancyType === 'new_position' ? 'flubber' : 'purple'
                    return {
                      type: 'dotTag' as const,
                      value: {
                        color,
                        label: item.vacancyTypeLabel,
                      },
                    }
                  },
                },
                {
                  label: i18n.t('ats.vacancies.table.columns.origin'),
                  render: (item: AtsVacanciesTableItemFragment) =>
                    item.originType
                      ? {
                          type: 'tag' as const,
                          value: {
                            label: item.originTypeLabel,
                            icon: item.originType === 'requisition' ? FileFilled : File,
                          },
                        }
                      : '-',
                },
                {
                  label: i18n.t('ats.vacancies.table.columns.candidate_employee'),
                  render: (item: AtsVacanciesTableItemFragment) =>
                    item.employee
                      ? renderPerson(item.employee)
                      : (item.application?.fullName ?? '-'),
                },
              ],
            },
          },
        ]}
        emptyStates={{
          'no-data': {
            emoji: '💼',
            title: i18n.t('ats.vacancies.table.empty_state.title'),
            description: i18n.t('ats.vacancies.table.empty_state.description'),
            actions: [
              // TODO: Who can create vacancies?
              {
                label: i18n.t('ats.vacancies.table.empty_state.add_vacancy_button'),
                icon: Add,
                variant: 'outline' as const,
                onClick: () => navigate(navigation.ats.hiringPlan.vacancies.new.fullPath()),
              },
            ].filter(Boolean),
          },
        }}
      />

      {showModal === VACANCIES_MODAL.ASSIGN_JOB_POSTING && selectedVacancyIds && (
        <AssignJobPostingModal
          vacancyIds={selectedVacancyIds.map(String)}
          onClose={closeModal}
          onSuccess={() => {
            clearSelectedItemsRef.current?.()
            closeModal()
          }}
        />
      )}

      {showModal === VACANCIES_MODAL.ASSIGN_HIRING_MANAGERS && selectedVacancyIds && (
        <AssignHiringManagersModal
          vacancyIds={selectedVacancyIds.map(String)}
          onClose={closeModal}
          onSuccess={() => {
            clearSelectedItemsRef.current?.()
            closeModal()
          }}
        />
      )}

      {showModal === VACANCIES_MODAL.ASSIGN_RECRUITERS && selectedVacancyIds && (
        <AssignRecruitersModal
          vacancyIds={selectedVacancyIds.map(String)}
          onClose={closeModal}
          onSuccess={() => {
            clearSelectedItemsRef.current?.()
            closeModal()
          }}
        />
      )}

      {showModal === VACANCIES_MODAL.CANCEL_VACANCY && selectedVacancyIds && (
        <CancelVacanciesDialog
          vacancyIds={selectedVacancyIds.map(String)}
          onClose={closeModal}
          onSuccess={() => {
            clearSelectedItemsRef.current?.()
            closeModal()
          }}
        />
      )}

      {showModal === VACANCIES_MODAL.ACTIVATE_VACANCY && selectedVacancyIds && (
        <ActivateVacanciesDialog
          vacancyIds={selectedVacancyIds.map(String)}
          onClose={closeModal}
          onSuccess={() => {
            clearSelectedItemsRef.current?.()
            closeModal()
          }}
        />
      )}
    </>
  )
}
